import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router'

import { submitUserPasswordPincode } from '../../../redux/authentication/authenticationActions'
import { Button } from '../../components'
import { LinkButton } from '../../components/@buttons/LinkButton/LinkButton'
import { TextInput } from '../../compositions'
import { LoginLayout } from '../../containers'
import { queryParamApiTokenKey, queryParamUserUuidKey, ResetPasswordFormData } from '../../entities/@forms/ResetPasswordForm/ResetPasswordForm'
import { localStorageTokenKey } from '../../entities/Authentication/Authentication'
import trans from '../../helpers/trans'
import { useTypedSelector } from '../../redux/store'
import authRoutes from '../../routes/subRoutes/authRoutes'
import { ResetPasswordFormErrors, validateResetPasswordFormData } from './validations'

import './ResetPassword.scss'

const ResetPassword: FC = (): ReactElement => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const { isLoading, newPasswordSuccess } = useTypedSelector(state => state.authenticationReducer)

    const [password, setPassword] = useState('')
    const [passwordRepeat, setPasswordRepeat] = useState('')
    const [pincode, setPincode] = useState('')
    const [pincodeRepeat, setPincodeRepeat] = useState('')
    const [formErrors, setFormErrors] = useState<ResetPasswordFormErrors>({})

    const loginPath = authRoutes.find(route => route.key === 'login')?.path || ''

    useEffect((): void => {
        const token = searchParams.get(queryParamApiTokenKey)
        const userId = searchParams.get(queryParamUserUuidKey)

        if (! token || ! userId) {
            console.error('[reset password] Missing token and/or userId')
            navigate(`/${loginPath}`)
        }
    }, [])

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault()

        const formData: ResetPasswordFormData = {
            password,
            passwordRepeat,
            pincode,
            pincodeRepeat,
        }

        const [errors, hasErrors] = validateResetPasswordFormData(formData)

        setFormErrors(errors)

        const token = searchParams.get(queryParamApiTokenKey)
        const userId = searchParams.get(queryParamUserUuidKey)

        if (! token || ! userId) {
            console.error('[reset password] Missing token and/or userId')
            navigate(`/${loginPath}`)
            return
        }

        if (! hasErrors) {
            localStorage.setItem(localStorageTokenKey, token)
            dispatch(submitUserPasswordPincode(userId, password, pincode))
        }
    }

    return (
        <LoginLayout>
            <div className="reset-password-page">
                <h4 className="reset-password-page__title">
                    {trans('pages.login.setPassword')}
                </h4>

                <p>{trans('pages.login.setPasswordIntro')}</p>

                <form className="reset-password-page__form" onSubmit={handleSubmit}>
                    <TextInput
                        hideLabel
                        required
                        autoComplete="new-password"
                        type="password"
                        label={trans('pages.login.password')}
                        placeholder={trans('pages.login.password')}
                        error={formErrors.password}
                        value={password}
                        onChange={setPassword}
                    />

                    <p className="reset-password-page__input-info">
                        {trans('pages.login.passwordRequirements')}
                    </p>

                    <TextInput
                        hideLabel
                        required
                        autoComplete="new-password"
                        type="password"
                        label={trans('pages.login.repeatPassword')}
                        placeholder={trans('pages.login.repeatPassword')}
                        error={formErrors.passwordRepeat}
                        value={passwordRepeat}
                        onChange={setPasswordRepeat}
                    />

                    <TextInput
                        hideLabel
                        type="password"
                        inputMode="numeric"
                        label={trans('pages.resetPassword.pincode')}
                        placeholder={trans('pages.resetPassword.pincode')}
                        error={formErrors.pincode}
                        value={pincode}
                        onChange={setPincode}
                    />

                    <p className="reset-password-page__input-info">
                        {trans('pages.login.pincodeRequirements')}
                    </p>

                    <TextInput
                        hideLabel
                        type="password"
                        inputMode="numeric"
                        label={trans('pages.resetPassword.confirmPincode')}
                        placeholder={trans('pages.resetPassword.confirmPincode')}
                        error={formErrors.pincodeRepeat}
                        value={pincodeRepeat}
                        onChange={setPincodeRepeat}
                    />

                    <div className="reset-password-page__buttons-container">
                        {! isLoading && ! newPasswordSuccess && (
                            <Button
                                type="submit"
                                text={trans('common.save')}
                                color="orange"
                                className="reset-password-page__submit-button"
                            />
                        )}

                        {isLoading && <div className="login-spinner" />}

                        {newPasswordSuccess && (
                            <div className="reset-password-page__buttons-succes-container">
                                <p className="login-paragraph-success">
                                    {trans('pages.login.passwordHasChanged')}
                                </p>

                                <LinkButton
                                    text={trans('pages.login.backToLogin')}
                                    to={`/${loginPath}`}
                                    color="link"
                                    className="login-button-back"
                                />
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </LoginLayout>
    )
}

export default ResetPassword
